import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiChevronRight,
  FiCheck,
  FiSearch,
  FiLoader,
  FiBook,
  FiFolder,
  FiLayers,
  FiGrid,
  FiFileText,
  FiLink,
  FiX,
} from "react-icons/fi";
import { listLibraries } from "../../admin/curriculum/curriculumLibrariesService";
import { createCurriculumService } from "../../admin/curriculum/curriculumService";
import { LAYOUT_TYPES } from "../../admin/curriculum/curriculumLibrariesService";
import { backdropDismiss } from "../../../utils/backdropDismiss";

// Unique key for a chosen curriculum resource (stable across re-opens).
function resourceKey(textbookId, resourceId) {
  return `${textbookId || ""}:${resourceId || ""}`;
}

// Display label for a leaf resource. Resources carry their own descriptive
// titles, and the folder above provides the topic context, so the resource name
// is shown as-is (e.g. a folder "Linear equations in one variable" holding
// resources titled "Easy" / "Medium" / "Hard").
function resourceLabel(res) {
  return res.title || res.name || "";
}

// ─── Reusable column ──────────────────────────────────────────────────────────
const MIN_COL_WIDTH = 180; // readable floor (and the floor for manual drag)
const AUTO_MAX_WIDTH = 440; // cap for content-based auto sizing (keeps modal tidy)
const MAX_COL_WIDTH = 640; // cap for manual drag

// Measure label width with a shared canvas so columns can size to their content.
let _measureCtx = null;
function measureLabelPx(text) {
  if (typeof document === "undefined") return 0;
  if (!_measureCtx) {
    const canvas = document.createElement("canvas");
    _measureCtx = canvas.getContext("2d");
    _measureCtx.font = "13px Inter, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
  }
  return _measureCtx.measureText(String(text || "")).width;
}

// Auto width for a column from its longest label, clamped to a tidy range.
// `chrome` accounts for padding, icons, checkbox, and chevron around the label.
function autoColumnWidth(labels, chrome = 96) {
  let max = 0;
  (labels || []).forEach((label) => {
    const w = measureLabelPx(label);
    if (w > max) max = w;
  });
  return Math.max(MIN_COL_WIDTH, Math.min(AUTO_MAX_WIDTH, Math.ceil(max) + chrome));
}

function BrowserColumn({ title, width, colId, onResizeStart, children }) {
  return (
    <div className="cbm-col" style={{ flex: `0 0 ${width}px`, width }}>
      {title && <div className="cbm-col-title">{title}</div>}
      <div className="cbm-col-body">{children}</div>
      {colId && onResizeStart && (
        <div
          className="cbm-col-resizer"
          onMouseDown={(e) => onResizeStart(colId, width, e)}
          role="separator"
          aria-orientation="vertical"
        />
      )}
    </div>
  );
}

function NodeRow({ icon, label, active, onClick }) {
  return (
    <button type="button" className={`cbm-row${active ? " cbm-row--active" : ""}`} onClick={onClick}>
      <span className="cbm-row-icon">{icon}</span>
      <span className="cbm-row-label">{label}</span>
      <FiChevronRight size={13} className="cbm-row-chevron" />
    </button>
  );
}

function LeafRow({ icon, label, isLink, selected, onClick }) {
  return (
    <button type="button" className={`cbm-row cbm-row--leaf${selected ? " cbm-row--selected" : ""}`} onClick={onClick}>
      <span className={`cbm-check${selected ? " cbm-check--on" : ""}`}>{selected && <FiCheck size={10} />}</span>
      <span className="cbm-row-icon">{icon}</span>
      <span className="cbm-row-label">{label}</span>
      {isLink && <FiLink size={11} className="cbm-row-linktag" />}
    </button>
  );
}

function StateBox({ children }) {
  return <div className="cbm-state">{children}</div>;
}

// ─── Main browser ───────────────────────────────────────────────────────────────
// A Mac Finder-style column browser over the unified curriculum libraries.
//   category libraries → Library / Category / Textbook / Resource
//   simple libraries   → Library / Textbook / Resource
export default function CurriculumBrowserModal({ selected = [], onConfirm, onClose }) {
  const [libraries, setLibraries] = useState([]);
  const [loadingLibs, setLoadingLibs] = useState(true);

  const [libSel, setLibSel] = useState(null);
  const [libContent, setLibContent] = useState(null); // categories[] (each with textbooks[])
  const [loadingContent, setLoadingContent] = useState(false);

  const [catSel, setCatSel] = useState(null);
  const [tbSel, setTbSel] = useState(null);
  const [tbResources, setTbResources] = useState(null);
  const [loadingRes, setLoadingRes] = useState(false);

  const [folderStack, setFolderStack] = useState([]); // [{ id, label }]
  const [search, setSearch] = useState("");

  // Per-column manual width overrides (drag). When a column has no override its
  // width auto-sizes to its content via effWidth(); dragging pins it.
  const [colWidths, setColWidths] = useState({});
  const effWidth = useCallback(
    (colId, labels) => (colWidths[colId] != null ? colWidths[colId] : autoColumnWidth(labels)),
    [colWidths]
  );
  const startResize = useCallback((colId, startWidth, e) => {
    e.preventDefault();
    const startX = e.clientX;
    const onMove = (ev) => {
      const next = Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, startWidth + (ev.clientX - startX)));
      setColWidths((prev) => ({ ...prev, [colId]: next }));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);

  // Auto-scroll the column strip to reveal the newly opened column as the user
  // navigates deeper (library → category → textbook → folder), Finder-style.
  const columnsRef = useRef(null);
  useEffect(() => {
    const el = columnsRef.current;
    if (!el) return undefined;
    const id = requestAnimationFrame(() => {
      el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
    });
    return () => cancelAnimationFrame(id);
    // Depends on each navigation level + the async-loaded content that grows the strip.
  }, [libSel?.id, catSel?.id, tbSel?.id, folderStack.length, libContent, tbResources, colWidths]);

  // Seed selection from previously-chosen curriculum items.
  const [selectedMap, setSelectedMap] = useState(() => {
    const map = new Map();
    (selected || []).forEach((item) => {
      if (item && item.type === "curriculum" && item.resourceId) {
        map.set(resourceKey(item.textbookId, item.resourceId), item);
      }
    });
    return map;
  });

  useEffect(() => {
    let alive = true;
    setLoadingLibs(true);
    listLibraries()
      .then((libs) => {
        if (alive) setLibraries(libs);
      })
      .catch(() => {
        if (alive) setLibraries([]);
      })
      .finally(() => {
        if (alive) setLoadingLibs(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const isCategoryLayout = libSel?.layoutType === LAYOUT_TYPES.CATEGORY;

  const selectLibrary = useCallback((lib) => {
    setLibSel(lib);
    setCatSel(null);
    setTbSel(null);
    setTbResources(null);
    setFolderStack([]);
    setLibContent(null);
    setLoadingContent(true);
    createCurriculumService(lib)
      .fetchLibrary()
      .then((categories) => setLibContent(categories || []))
      .catch(() => setLibContent([]))
      .finally(() => setLoadingContent(false));
  }, []);

  const selectCategory = useCallback((cat) => {
    setCatSel(cat);
    setTbSel(null);
    setTbResources(null);
    setFolderStack([]);
  }, []);

  const selectTextbook = useCallback(
    (tb) => {
      setTbSel(tb);
      setTbResources(null);
      setFolderStack([]);
      setLoadingRes(true);
      createCurriculumService(libSel)
        .fetchTextbookDetail(tb._categoryId, tb.id)
        .then((detail) => setTbResources(detail.resources || []))
        .catch(() => setTbResources([]))
        .finally(() => setLoadingRes(false));
    },
    [libSel]
  );

  const toggleResource = useCallback(
    (resource, folderLabels) => {
      const url = resource.url || "";
      const parts = [
        libSel?.name,
        isCategoryLayout ? catSel?.name : null,
        tbSel?.title || tbSel?.name,
        ...folderLabels,
        resourceLabel(resource),
      ].filter(Boolean);
      const item = {
        label: parts.join(" / "),
        url,
        type: "curriculum",
        librarySlug: libSel?.slug || "",
        libraryId: libSel?.id || "",
        categoryId: tbSel?._categoryId || "",
        textbookId: tbSel?.id || "",
        resourceId: resource.id || "",
        resourceType: resource.type || "",
      };
      setSelectedMap((prev) => {
        const next = new Map(prev);
        const key = resourceKey(tbSel?.id, resource.id);
        if (next.has(key)) next.delete(key);
        else next.set(key, item);
        return next;
      });
    },
    [libSel, catSel, tbSel, isCategoryLayout]
  );

  const q = search.trim().toLowerCase();
  const matchesQuery = (text) => !q || String(text || "").toLowerCase().includes(q);

  // Textbooks visible for the current selection.
  const textbooks = useMemo(() => {
    if (!libContent) return [];
    if (isCategoryLayout) {
      if (!catSel) return [];
      const cat = libContent.find((c) => c.id === catSel.id);
      return (cat?.textbooks || []).map((tb) => ({ ...tb, _categoryId: cat.id }));
    }
    // Simple layout: flatten every category's textbooks (there is normally one).
    return libContent.flatMap((cat) => (cat.textbooks || []).map((tb) => ({ ...tb, _categoryId: cat.id })));
  }, [libContent, isCategoryLayout, catSel]);

  // Resources grouped by folder for the column at each depth.
  const resourcesByFolder = useCallback(
    (folderId) =>
      (tbResources || []).filter((r) => (folderId ? r.folderId === folderId : !r.folderId)),
    [tbResources]
  );

  const openFolder = useCallback((columnDepth, folder) => {
    setFolderStack((prev) => prev.slice(0, columnDepth).concat({ id: folder.id, label: folder.title || folder.name }));
  }, []);

  const handleConfirm = () => onConfirm([...selectedMap.values()]);

  // Build the list of resource columns (top level + one per opened folder).
  const resourceColumnFolderIds = useMemo(() => [null, ...folderStack.map((f) => f.id)], [folderStack]);

  const renderResourceColumn = (folderId, columnDepth) => {
    const folderLabels = folderStack.slice(0, columnDepth).map((f) => f.label);
    const items = resourcesByFolder(folderId).filter(
      (r) => r.type === "separator" || matchesQuery(r.title || r.name)
    );

    const colId = `res-${columnDepth}`;
    const resWidth = effWidth(colId, items.map((r) => resourceLabel(r)));

    if (loadingRes && columnDepth === 0) {
      return (
        <BrowserColumn title="Resources" colId={colId} width={resWidth} onResizeStart={startResize}>
          <StateBox><FiLoader className="cbm-spin" size={14} /> Loading…</StateBox>
        </BrowserColumn>
      );
    }

    return (
      <BrowserColumn
        title={columnDepth === 0 ? "Resources" : folderStack[columnDepth - 1]?.label}
        colId={colId}
        width={resWidth}
        onResizeStart={startResize}
      >
        {items.length === 0 ? (
          <StateBox>No resources</StateBox>
        ) : (
          items.map((res) => {
            if (res.type === "separator") {
              return (
                <div key={res.id} className="cbm-separator">{res.title || res.name}</div>
              );
            }
            if (res.type === "folder") {
              const active = folderStack[columnDepth]?.id === res.id;
              return (
                <NodeRow
                  key={res.id}
                  icon={<FiFolder size={13} />}
                  label={res.title || res.name}
                  active={active}
                  onClick={() => openFolder(columnDepth, res)}
                />
              );
            }
            const key = resourceKey(tbSel?.id, res.id);
            return (
              <LeafRow
                key={res.id}
                icon={<FiFileText size={13} />}
                label={resourceLabel(res)}
                isLink={res.type === "link"}
                selected={selectedMap.has(key)}
                onClick={() => toggleResource(res, folderLabels)}
              />
            );
          })
        )}
      </BrowserColumn>
    );
  };

  const selectedItems = [...selectedMap.values()];

  return (
    <div className="cbm-backdrop" {...backdropDismiss(onClose)}>
      <div className="cbm-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="cbm-header">
          <div>
            <h2 className="cbm-title">Browse Curriculum</h2>
            <p className="cbm-subtitle">Navigate any library and pick the resources used this session.</p>
          </div>
          <button className="cbm-close" onClick={onClose} aria-label="Close">
            <FiX size={15} />
          </button>
        </div>

        {/* Search */}
        <div className="cbm-search-row">
          <FiSearch size={13} className="cbm-search-icon" />
          <input
            className="cbm-search"
            placeholder="Filter by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Columns */}
        <div className="cbm-columns" ref={columnsRef}>
          {/* Column 1: libraries */}
          <BrowserColumn
            title="Libraries"
            colId="libraries"
            width={effWidth("libraries", libraries.map((l) => l.name))}
            onResizeStart={startResize}
          >
            {loadingLibs ? (
              <StateBox><FiLoader className="cbm-spin" size={14} /> Loading…</StateBox>
            ) : libraries.length === 0 ? (
              <StateBox>No libraries</StateBox>
            ) : (
              libraries
                .filter((lib) => matchesQuery(lib.name))
                .map((lib) => (
                  <NodeRow
                    key={lib.id}
                    icon={lib.layoutType === LAYOUT_TYPES.CATEGORY ? <FiLayers size={13} /> : <FiGrid size={13} />}
                    label={lib.name}
                    active={libSel?.id === lib.id}
                    onClick={() => selectLibrary(lib)}
                  />
                ))
            )}
          </BrowserColumn>

          {/* Column 2: categories (category layout) */}
          {libSel && isCategoryLayout && (
            <BrowserColumn
              title="Categories"
              colId="categories"
              width={effWidth("categories", (libContent || []).map((c) => c.name))}
              onResizeStart={startResize}
            >
              {loadingContent ? (
                <StateBox><FiLoader className="cbm-spin" size={14} /> Loading…</StateBox>
              ) : (libContent || []).length === 0 ? (
                <StateBox>No categories</StateBox>
              ) : (
                (libContent || [])
                  .filter((cat) => matchesQuery(cat.name))
                  .map((cat) => (
                    <NodeRow
                      key={cat.id}
                      icon={<FiFolder size={13} />}
                      label={cat.name}
                      active={catSel?.id === cat.id}
                      onClick={() => selectCategory(cat)}
                    />
                  ))
              )}
            </BrowserColumn>
          )}

          {/* Textbooks column */}
          {libSel && (!isCategoryLayout || catSel) && (
            <BrowserColumn
              title="Textbooks"
              colId="textbooks"
              width={effWidth("textbooks", textbooks.map((tb) => tb.title || tb.name))}
              onResizeStart={startResize}
            >
              {loadingContent ? (
                <StateBox><FiLoader className="cbm-spin" size={14} /> Loading…</StateBox>
              ) : textbooks.filter((tb) => matchesQuery(tb.title || tb.name)).length === 0 ? (
                <StateBox><FiBook size={20} /><span>No textbooks</span></StateBox>
              ) : (
                textbooks
                  .filter((tb) => matchesQuery(tb.title || tb.name))
                  .map((tb) => (
                    <NodeRow
                      key={tb.id}
                      icon={<FiBook size={13} />}
                      label={tb.title || tb.name}
                      active={tbSel?.id === tb.id}
                      onClick={() => selectTextbook(tb)}
                    />
                  ))
              )}
            </BrowserColumn>
          )}

          {/* Resource columns (top level + opened folders) */}
          {tbSel && resourceColumnFolderIds.map((folderId, depth) => (
            <React.Fragment key={`rescol-${depth}-${folderId || "root"}`}>
              {renderResourceColumn(folderId, depth)}
            </React.Fragment>
          ))}
        </div>

        {/* Footer */}
        <div className="cbm-footer">
          <div className="cbm-selected-count">
            {selectedItems.length > 0
              ? `${selectedItems.length} resource${selectedItems.length !== 1 ? "s" : ""} selected`
              : "No resources selected"}
          </div>
          <div className="cbm-footer-actions">
            <button className="cbm-btn-ghost" onClick={onClose}>Cancel</button>
            <button className="cbm-btn-confirm" onClick={handleConfirm}>
              Add Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
